import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrandLogo from "../shared/BrandLogo";
import { SITE_SETTINGS } from "../../data/site";
import { useServices } from "../../context/ServicesContext";
import { generateWhatsAppUrl } from "../../lib/whatsapp";
import MobileMenu from "./MobileMenu";
import { cn } from "../../lib/utils";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mega menu on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={cn(
          "fixed inset-x-0 top-0 z-40 transition-[background,border,backdrop-filter] duration-300",
          scrolled
            ? "bg-ink/85 backdrop-blur-xl border-b border-charcoal/60"
            : "bg-ink/60 backdrop-blur-md border-b border-transparent"
        )}
      >
        <div className="container-emilio flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2 group"
            aria-label="Emilio's Solutions — Home"
          >
            <BrandLogo className="h-8 md:h-10" />
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-6"
            data-testid="header-nav"
          >
            {SITE_SETTINGS.navigation.map((item) => {
              if (item.hasMega) {
                return (
                  <div
                    key={item.to}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <NavLink
                      to={item.to}
                      data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                      className={({ isActive }) =>
                        cn(
                          "relative inline-flex items-center gap-1 text-sm font-medium py-2 transition-colors",
                          isActive ? "text-emerald" : "text-white/90 hover:text-emerald"
                        )
                      }
                    >
                      {item.label}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </NavLink>
                    <AnimatePresence>
                      {servicesOpen && <MegaMenu />}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.to.replace(/\//g, "-") || "home"}`}
                  className={({ isActive }) =>
                    cn(
                      "relative text-sm font-medium py-2 transition-colors",
                      isActive
                        ? "text-emerald"
                        : "text-white/90 hover:text-emerald"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      {isActive && (
                        <motion.span
                          layoutId="active-dot"
                          className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-emerald rounded-full"
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-3">
            {/* The brief puts a Get a Quote action in the header beside WhatsApp. */}
            <Link
              to="/contact"
              data-testid="header-quote-cta"
              className="hidden sm:inline-flex items-center rounded-full border border-emerald text-emerald px-4 md:px-5 py-2.5 text-sm font-semibold hover:bg-emerald hover:text-ink transition-colors"
            >
              Get a Quote
            </Link>
            <a
              href={generateWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-emerald text-ink px-4 md:px-5 py-2.5 text-sm font-semibold hover:bg-emerald-light transition-colors duration-300"
            >
              <WhatsIcon className="w-4 h-4" />
              <span className="hidden md:inline">Book via WhatsApp</span>
              <span className="md:hidden">Book</span>
            </a>
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-charcoal text-white hover:border-emerald hover:text-emerald transition-colors"
              aria-label="Open menu"
              data-testid="header-mobile-toggle"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="rule-green opacity-30" />
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}

function MegaMenu() {
  const { nav } = useServices();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
      className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
      data-testid="services-mega-menu"
    >
      {/*
        Built from the published services and their groupings, so adding a
        service in the admin panel files it into the right column — there is no
        menu to maintain separately.
      */}
      <div className="w-[760px] bg-ink border border-charcoal shadow-2xl p-6">
        <div className="grid grid-cols-3 gap-6">
          {nav.map((section) => (
            <div key={section.group}>
              <div className="text-eyebrow font-display font-bold uppercase tracking-[0.18em] text-emerald mb-3">
                {section.group}
              </div>
              <div className="flex flex-col gap-1">
                {section.items.map((item) => (
                  <Link
                    key={item.route}
                    to={item.route}
                    className="group/item py-2 px-2 -mx-2 rounded hover:bg-charcoal/40 transition-colors"
                    data-testid={`mega-service-${item.route.replace("/", "")}`}
                  >
                    <div className="text-white text-sm font-semibold group-hover/item:text-emerald transition-colors">
                      {item.name}
                    </div>
                    {item.shortDescription && (
                      <div className="text-mutedtext text-xs mt-0.5 line-clamp-2">
                        {item.shortDescription}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-charcoal">
          <Link
            to="/services"
            className="text-sm text-emerald hover:text-white inline-flex items-center gap-2"
          >
            View all services <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

function WhatsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M20.5 3.5A11 11 0 0 0 3.7 17.2L2 22l4.9-1.6a11 11 0 0 0 5.2 1.3h.1a11 11 0 0 0 8.3-18.2Zm-8.4 17a9 9 0 0 1-4.6-1.3l-.3-.2-2.9.9.9-2.8-.2-.3a9 9 0 1 1 7.1 3.7Zm5-6.7c-.3-.1-1.8-.9-2-1s-.5-.1-.7.2-.8 1-.9 1.1-.3.2-.6.1a7.3 7.3 0 0 1-2.2-1.4 8 8 0 0 1-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.4.2-.4v-.4l-1-2.3c-.2-.6-.4-.5-.6-.5h-.6c-.2 0-.5.1-.8.4a3.6 3.6 0 0 0-1.1 2.7c0 1.6 1.1 3.1 1.3 3.3.2.3 2.3 3.4 5.5 4.8.8.3 1.4.5 1.8.6.8.3 1.5.2 2 .1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4l-.5-.3Z" />
    </svg>
  );
}
